/**
 * Mora illustration set — hand-drawn two-tone editorial style.
 *
 * Iteration 2 (polish pass): genuine hand-drawn quality — fountain-pen feel.
 * Strokes vary in width within a single drawing, beziers wobble off geometric
 * ideal, compositions sit asymmetrically, and the warm-accent fill is always
 * an organic blob (never a perfect circle/rectangle).
 *
 * Two-tone system:
 * - Black ink:   stroke-foreground (auto adapts light/dark)
 * - Warm accent: fill-[hsl(var(--illustration-accent))] — Mora amber-gold
 *                (NOT Anthropic terracotta) — see src/index.css token
 *
 * Stroke-width vocabulary (apply within EVERY illustration):
 * - 2.0  primary outline (THE subject)
 * - 1.5  secondary structural lines
 * - 1.0  hairline detail / asides
 *
 * Used in empty states, hero cards, and onboarding surfaces.
 * Render via parent text-foreground/85+ class for readable ink in both themes.
 */

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

const SMALL = {
  viewBox: '0 0 80 56',
  fill: 'none' as const,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  xmlns: 'http://www.w3.org/2000/svg',
};

const HERO = {
  viewBox: '0 0 120 80',
  fill: 'none' as const,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  xmlns: 'http://www.w3.org/2000/svg',
};

const FULL = {
  viewBox: '0 0 120 120',
  fill: 'none' as const,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  xmlns: 'http://www.w3.org/2000/svg',
};

const ACCENT = 'fill-[hsl(var(--illustration-accent))] stroke-none';
const INK = 'stroke-foreground';
const INK_FILL = 'fill-foreground stroke-none';

/* 1. CreatePostLineIcon — wobbly post card with warm-accent sticker blob */
export function CreatePostLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm sticker blob — slightly oblong, off-axis */}
      <path
        d="M51 11 C 56 10, 60 13, 60 17 C 59.6 21, 55 23, 51 21 C 47 19, 47 13, 51 11 Z"
        className={ACCENT}
      />
      {/* card outline — wobble at corners */}
      <path
        d="M14 19 C 14 17.4, 15.4 16.2, 17 16.3 L 44 16.5 C 45.6 16.6, 46.6 17.9, 46.5 19.5 L 46 44 C 45.9 45.6, 44.5 46.7, 42.9 46.6 L 16 46 C 14.4 45.9, 13.5 44.6, 13.6 43 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* text lines — varying length + slight droop */}
      <path d="M19 25 C 25 24.7, 32 25.1, 39 24.8" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M19 31 C 24 30.8, 30 31, 36 30.9" className={INK} strokeWidth={1.2} fill="none" />
      <path d="M19 37 C 23 36.8, 27 37, 30 36.9" className={INK} strokeWidth={1.2} fill="none" />
      {/* punctuation dot */}
      <circle cx="42" cy="37" r="1.4" className={INK_FILL} />
    </svg>
  );
}

/* 2. AIGenerateLineIcon — confident sweeping curve + LARGE organic 8-ray sparkle */
export function AIGenerateLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent organic sparkle — 8 imperfect rays */}
      <path
        d="M55 8 L 57 19 L 64 21 L 67 17 L 65 25 L 70 28 L 60 27 L 57.5 35 L 55 27 L 47 30 L 50 23 L 44 19 L 52 19 L 53 12 Z"
        className={ACCENT}
      />
      {/* confident sweeping curve — wobble in middle */}
      <path
        d="M14 46 C 18 44, 22 42, 26 38 C 28 36, 30 32, 33 30 C 36 28, 40 26, 43 22"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* trailing wisp — hairline */}
      <path d="M16 49 C 22 48, 28 47, 34 46" className={INK} strokeWidth={1} fill="none" />
      {/* scatter dots */}
      <circle cx="14" cy="46" r="1.6" className={INK_FILL} />
      <circle cx="42" cy="14" r="1.2" className={INK_FILL} />
      <circle cx="68" cy="36" r="1" className={INK_FILL} />
    </svg>
  );
}

/* 3. TemplateLineIcon — three overlapping wobbly cards, middle filled warm */
export function TemplateLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm middle card — organic edges */}
      <path
        d="M28 17 C 28 16, 28.9 15.3, 30 15.4 L 50 15.6 C 51.2 15.7, 51.9 16.6, 51.8 17.7 L 51.3 39.5 C 51.2 40.7, 50.2 41.4, 49 41.3 L 29 40.9 C 28 40.8, 27.3 39.9, 27.4 38.8 Z"
        className={ACCENT}
      />
      {/* back card — secondary stroke */}
      <path
        d="M16 13 C 16 12, 16.7 11.4, 17.6 11.5 L 38 11.7 C 39 11.8, 39.7 12.6, 39.6 13.5 L 39.1 35 C 39 36, 38.2 36.6, 37.3 36.5 L 16.8 36.1 C 15.9 36, 15.3 35.3, 15.4 34.4 Z"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* front card — primary stroke */}
      <path
        d="M40 21 C 40 19.9, 40.7 19.2, 41.6 19.3 L 62 19.5 C 63.1 19.6, 63.8 20.5, 63.7 21.6 L 63.2 43.5 C 63.1 44.7, 62.2 45.4, 61 45.3 L 40.8 44.9 C 39.8 44.8, 39.1 43.9, 39.2 42.8 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* punctuation dot at corner intersection */}
      <circle cx="40" cy="21" r="1.3" className={INK_FILL} />
    </svg>
  );
}

/* 4. ProductLineIcon — wobbly ink carton, warm-accent shipping label, hang tag
 *
 * Redrawn 2026-08-27. It used to invert the two-tone system this file's own
 * docblock sets out: the ACCENT fill was the entire box and there was no ink
 * outline at all, so at the 80px empty-state band it rendered as a flat
 * saturated rectangle with a disconnected hook floating beside it — a coloured
 * slab where every one of its 26 siblings draws an ink subject carrying a small
 * contained accent blob. Seen on `/products`, the surface the founder called a
 * blank spot. The subject is ink at 2.0 again; the accent is a label on its
 * face; the tag string now actually ties to the carton's corner.
 */
export function ProductLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent shipping label — organic blob, sits off-centre on the face */}
      <path
        d="M24 35.5 C 24 34.4, 24.9 33.6, 26 33.7 L 35.5 34.1 C 36.7 34.2, 37.5 35.2, 37.3 36.4 L 36.9 40.2 C 36.8 41.3, 35.8 42.1, 34.7 42 L 25.1 41.5 C 24 41.4, 23.2 40.5, 23.3 39.4 Z"
        className={ACCENT}
      />
      {/* carton — primary outline 2.0, corners wobble off the geometric ideal */}
      <path
        d="M18.4 25 C 18.3 23.6, 19.4 22.5, 20.8 22.5 L 49.6 22.9 C 51 23, 52.1 24.2, 52 25.6 L 51.2 44.4 C 51.1 45.8, 50 46.8, 48.6 46.7 L 20 46.2 C 18.7 46.1, 17.7 45, 17.8 43.6 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* lid seam — secondary structure, 1.5 */}
      <path
        d="M18.1 29.8 C 29 30.7, 41 30.8, 51.8 30.1"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* tape tab down the seam — hairline aside */}
      <path d="M34.8 22.7 L 34.5 30.4" className={INK} strokeWidth={1} fill="none" />
      {/* hang-tag string — tied to the carton corner, confident curve at 1.5 */}
      <path
        d="M51.8 25.4 C 55.4 24.6, 58.6 21.8, 60.2 18.4"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* knot where the string meets the carton */}
      <circle cx="51.8" cy="25.4" r="1.6" className={INK_FILL} />
      {/* tag eyelet — wobbly tiny circle */}
      <path
        d="M59.1 16.9 C 59.1 16, 59.8 15.4, 60.7 15.5 C 61.6 15.6, 62.2 16.3, 62.1 17.2 C 62 18, 61.3 18.5, 60.4 18.4 C 59.6 18.3, 59.1 17.7, 59.1 16.9 Z"
        className={INK}
        strokeWidth={1.2}
        fill="none"
      />
    </svg>
  );
}

/* 5. FillCalendarLineIcon — wobbly calendar grid, one cell filled warm */
export function FillCalendarLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm filled cell — organic blob */}
      <path
        d="M37 26 C 37 25.4, 37.4 25, 38 25 L 47 25.1 C 47.6 25.2, 48 25.6, 48 26.2 L 47.8 33 C 47.7 33.6, 47.3 34, 46.7 34 L 37.6 33.9 C 37 33.8, 36.7 33.4, 36.7 32.8 Z"
        className={ACCENT}
      />
      {/* calendar outer — primary */}
      <path
        d="M14 17 C 14 15.8, 14.8 15, 16 15 L 64 15.3 C 65.2 15.4, 66 16.3, 65.9 17.5 L 65.5 44 C 65.4 45.3, 64.5 46.1, 63.3 46 L 15 45.7 C 13.8 45.6, 13 44.7, 13.1 43.5 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* header rule — secondary */}
      <path d="M14 25 C 30 24.6, 50 25.1, 66 24.8" className={INK} strokeWidth={1.5} fill="none" />
      {/* internal grid lines — hairline */}
      <path d="M27 17 C 27 22, 27.1 36, 27 45" className={INK} strokeWidth={1} fill="none" />
      <path d="M40 17 C 40 22, 40.1 36, 40 45" className={INK} strokeWidth={1} fill="none" />
      <path d="M52 17 C 52 22, 52.1 36, 52 45" className={INK} strokeWidth={1} fill="none" />
      <path d="M14 35 C 30 34.7, 50 35.1, 66 34.9" className={INK} strokeWidth={1} fill="none" />
      {/* binding rings */}
      <path d="M22 11 L 22 18" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M58 11 L 58 18" className={INK} strokeWidth={1.5} fill="none" />
    </svg>
  );
}

/* 6. CampaignLineIcon — megaphone outline + radiating lines + warm-accent dot */
export function CampaignLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent broadcast blob */}
      <path
        d="M54 18 C 58 17, 61 19, 61 22 C 60.7 25, 57 26, 54 25 C 51 24, 51 19, 54 18 Z"
        className={ACCENT}
      />
      {/* megaphone — primary, asymmetric tilt */}
      <path
        d="M14 28 C 14 26.8, 14.9 26, 16 26 L 26 26.5 L 46 13 C 47.2 12.4, 48.4 13.1, 48.3 14.5 L 47 38 C 46.9 39.4, 45.7 40, 44.5 39.4 L 26 31 L 17 31 C 15.8 31, 14.9 30.2, 14.9 29 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* hanging string — secondary */}
      <path d="M22 31 C 22.5 36, 23.5 41, 25 44" className={INK} strokeWidth={1.5} fill="none" />
      {/* radiating sound lines — hairline */}
      <path d="M64 13 C 67 12, 69 13, 71 15" className={INK} strokeWidth={1} fill="none" />
      <path d="M66 28 C 69 28, 71 29, 73 31" className={INK} strokeWidth={1} fill="none" />
      {/* punctuation */}
      <circle cx="68" cy="20" r="1.2" className={INK_FILL} />
    </svg>
  );
}

/* 7. MediaLibraryLineIcon — photo-frame + warm-accent organic blob inside */
export function MediaLibraryLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm "found photo" blob — clearly hand-drawn shape */}
      <path
        d="M44 25 C 50 23, 55 26, 55 31 C 54.6 36, 48 38, 43 36 C 38 33, 38 27, 44 25 Z"
        className={ACCENT}
      />
      {/* frame — primary outline */}
      <path
        d="M14 13 C 14 11.8, 14.8 11, 16 11 L 64 11.3 C 65.2 11.4, 66 12.3, 65.9 13.5 L 65.4 43 C 65.3 44.3, 64.4 45.1, 63.2 45 L 15 44.7 C 13.8 44.6, 13 43.7, 13.1 42.5 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* horizon-mountain interior line — secondary */}
      <path
        d="M16 38 C 22 32, 27 30, 31 34 C 34 37, 37 41, 41 40"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* sun circle — wobbly */}
      <path
        d="M19 21 C 19 19.5, 20.5 18.4, 22 18.5 C 23.5 18.6, 24.7 19.9, 24.5 21.4 C 24.3 22.8, 23 23.8, 21.5 23.5 C 20 23.2, 19 22.4, 19 21 Z"
        className={INK}
        strokeWidth={1.2}
        fill="none"
      />
    </svg>
  );
}

/* 8. AnalyticsLineIcon — three rising bars + warm-accent dot at peak */
export function AnalyticsLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent peak indicator blob */}
      <path
        d="M55 11 C 60 10, 64 13, 63.5 17 C 63 20.5, 58 21.5, 55 19.5 C 52 17.5, 52 12, 55 11 Z"
        className={ACCENT}
      />
      {/* bars — varying weight + slight wobble in shaft */}
      <path d="M22 44 C 22 40, 22.1 37, 22 35" className={INK} strokeWidth={2.5} fill="none" />
      <path d="M34 44 C 34 38, 34.1 32, 34 28" className={INK} strokeWidth={2.5} fill="none" />
      <path d="M46 44 C 46 36, 46.1 28, 46 22" className={INK} strokeWidth={2.5} fill="none" />
      <path d="M58 44 C 58 32, 58.1 24, 58 18" className={INK} strokeWidth={2.5} fill="none" />
      {/* baseline — hairline, slight droop */}
      <path d="M14 47 C 28 46.6, 50 47.2, 66 46.8" className={INK} strokeWidth={1} fill="none" />
      {/* trend arrow connecting peak to dot */}
      <path d="M58 18 C 58 16, 56 14, 55 13" className={INK} strokeWidth={1} fill="none" />
    </svg>
  );
}

/* 9. EngagementLineIcon — speech bubble + warm-accent heart blob */
export function EngagementLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent heart — organic, asymmetric */}
      <path
        d="M37 22 C 39 19, 43 19, 44 23 C 45 19, 49 19, 51 23 C 52 26, 48 31, 44 34 C 40 31, 36 26, 37 22 Z"
        className={ACCENT}
      />
      {/* speech bubble — primary */}
      <path
        d="M14 18 C 14 16.4, 15 15.4, 16.5 15.4 L 60.5 15.7 C 62 15.8, 63 17, 63 18.5 L 62.5 36.5 C 62.5 38, 61.4 39, 60 38.9 L 30 38.6 L 20 46 L 21 38.4 L 16 38.3 C 14.5 38.3, 13.5 37.2, 13.5 35.7 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* small punctuation dot at tail */}
      <circle cx="20" cy="46" r="1.2" className={INK_FILL} />
    </svg>
  );
}

/* 10. NotFoundLineIcon (120×120) — BROKEN COMPASS, asymmetric, bent needle */
export function NotFoundLineIcon(props: IconProps) {
  return (
    <svg {...FULL} {...props}>
      {/* warm-accent organic pivot blob — slightly off-center */}
      <path
        d="M55 60 C 55 56, 60 54, 64 56 C 67.5 58, 67.5 64, 64 66 C 60 68, 55 65, 55 60 Z"
        className={ACCENT}
      />
      {/* outer compass ring — wobbly bezier circle, off-center */}
      <path
        d="M22 62 C 21 41, 39 22, 60 21 C 81 21.5, 99 39, 100 60 C 99.5 82, 82 100, 60 99 C 39 98, 23 82, 22 62 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* inner ring — hairline, slightly different center */}
      <path
        d="M30 62 C 29.5 46, 44 31, 60 31 C 76 31.5, 90 46, 90 62 C 89.5 78, 76 92, 60 91 C 44 90, 31 78, 30 62 Z"
        className={INK}
        strokeWidth={1}
        fill="none"
      />
      {/* BENT needle — pointing nowhere, kink in middle */}
      <path
        d="M40 78 C 48 70, 56 64, 62 60 C 64 58, 61 54, 58 52 C 54 49, 48 47, 42 44"
        className={INK}
        strokeWidth={2.5}
        fill="none"
      />
      {/* needle tail — secondary, opposite direction */}
      <path d="M64 62 C 70 66, 76 72, 80 80" className={INK} strokeWidth={2} fill="none" />
      {/* confused scribble cluster — top-right */}
      <path
        d="M88 32 C 90 30, 92 32, 91 34 C 90 36, 88 35, 89 33"
        className={INK}
        strokeWidth={1}
        fill="none"
      />
      <path
        d="M94 28 C 95 27, 96 28, 95.5 29 C 95 30, 94 29.5, 94.5 28.5"
        className={INK}
        strokeWidth={1}
        fill="none"
      />
      {/* cardinal tick marks — only N visible, others missing/asymmetric */}
      <path d="M60 14 L 60 21" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M14 62 L 22 62" className={INK} strokeWidth={1} fill="none" />
      <path d="M99 60 L 106 60" className={INK} strokeWidth={1} fill="none" />
      {/* small ink dots — confusion */}
      <circle cx="84" cy="36" r="1.2" className={INK_FILL} />
      <circle cx="98" cy="32" r="1" className={INK_FILL} />
    </svg>
  );
}

/* 11. EmptyChartLineIcon — dashed bars, NO warm fill */
export function EmptyChartLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      <path
        d="M22 44 L 22 36"
        className="stroke-foreground/40"
        strokeWidth={2}
        strokeDasharray="2 3"
        fill="none"
      />
      <path
        d="M34 44 L 34 30"
        className="stroke-foreground/40"
        strokeWidth={2}
        strokeDasharray="2 3"
        fill="none"
      />
      <path
        d="M46 44 L 46 26"
        className="stroke-foreground/40"
        strokeWidth={2}
        strokeDasharray="2 3"
        fill="none"
      />
      <path
        d="M58 44 L 58 22"
        className="stroke-foreground/40"
        strokeWidth={2}
        strokeDasharray="2 3"
        fill="none"
      />
      <path d="M14 47 C 28 46.7, 50 47.2, 66 46.8" className={INK} strokeWidth={1.5} fill="none" />
    </svg>
  );
}

/* 12. BrandKitLineIcon — three paint dabs, middle filled warm + brush stroke */
export function BrandKitLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm middle blob */}
      <path
        d="M34 22 C 41 21, 47 23, 47 28 C 46.5 33, 41 35, 35 33 C 30 31, 29 24, 34 22 Z"
        className={ACCENT}
      />
      {/* left blob — secondary */}
      <path
        d="M18 14 C 24 13, 30 15, 30 19 C 29.6 23, 24 25, 19 23 C 15 21, 14 15, 18 14 Z"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* right blob — secondary */}
      <path
        d="M50 30 C 56 29, 62 31, 62 35 C 61.6 39, 56 41, 51 39 C 47 37, 46 31, 50 30 Z"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* brush sweep — primary, with wobble */}
      <path
        d="M16 44 C 26 42.5, 38 43, 50 43.5 C 56 43.7, 62 43, 66 42"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* tail tip dot */}
      <circle cx="66" cy="42" r="1.2" className={INK_FILL} />
    </svg>
  );
}

/* 13. CalendarEmptyLineIcon — calendar shape with warm-accent clock orbiting corner */
export function CalendarEmptyLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      <g transform="translate(3 0)">
        {/* warm-accent clock blob — corner orbit */}
        <path
          d="M55 12 C 60 11, 63 14, 62.5 17 C 62 20, 57 21, 54 19 C 51 17, 51 13, 55 12 Z"
          className={ACCENT}
        />
        {/* calendar — primary */}
        <path
          d="M14 19 C 14 17.8, 14.8 17, 16 17 L 50 17.2 C 51.2 17.3, 52 18.2, 51.9 19.4 L 51.5 44 C 51.4 45.3, 50.5 46.1, 49.3 46 L 15 45.7 C 13.8 45.6, 13 44.7, 13.1 43.5 Z"
          className={INK}
          strokeWidth={2}
          fill="none"
        />
        {/* header rule */}
        <path
          d="M14 26 C 26 25.6, 38 26.1, 52 25.8"
          className={INK}
          strokeWidth={1.5}
          fill="none"
        />
        {/* clock hands — hairline */}
        <path d="M58 14 L 58 17" className={INK} strokeWidth={1} fill="none" />
        <path d="M58 16 L 60.5 16" className={INK} strokeWidth={1} fill="none" />
      </g>
    </svg>
  );
}

/* 14. PlanHeroLineIcon (120×80) — wide calendar strip with checked + warm-filled cells */
export function PlanHeroLineIcon(props: IconProps) {
  return (
    <svg {...HERO} {...props}>
      {/* warm filled cell — organic */}
      <path
        d="M65 28 C 65 27.2, 65.6 26.6, 66.5 26.6 L 86 26.7 C 87 26.8, 87.6 27.5, 87.5 28.5 L 87.2 55.5 C 87.1 56.5, 86.4 57.1, 85.5 57 L 66 56.8 C 65.1 56.7, 64.5 56, 64.6 55 Z"
        className={ACCENT}
      />
      {/* calendar outer — primary */}
      <path
        d="M16 22 C 16 20.6, 17 19.8, 18 20 L 102 20.4 C 103.4 20.5, 104.2 21.6, 104 23 L 103.5 60.5 C 103.4 61.9, 102.4 62.7, 101 62.5 L 17.3 62.1 C 16 62, 15.3 61, 15.5 59.6 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* header rule */}
      <path d="M16 28 C 40 27.7, 80 28.2, 104 27.8" className={INK} strokeWidth={1.5} fill="none" />
      {/* column dividers — hairline */}
      <path d="M37 20 C 37 30, 37.1 50, 37 62" className={INK} strokeWidth={1} fill="none" />
      <path d="M58 20 C 58 30, 58.1 50, 58 62" className={INK} strokeWidth={1} fill="none" />
      <path d="M79 20 C 79 30, 79.1 50, 79 62" className={INK} strokeWidth={1} fill="none" />
      {/* checkmarks — primary weight */}
      <path d="M22 42 C 25 45, 28 42, 32 38" className={INK} strokeWidth={2} fill="none" />
      <path d="M43 42 C 46 45, 49 42, 53 38" className={INK} strokeWidth={2} fill="none" />
    </svg>
  );
}

/* 15. WebsiteScanLineIcon — SMALLER globe + bigger warm "found" blob offset to side + scan sweep */
export function WebsiteScanLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* BIG warm "found result" blob — offset right, organic */}
      <path
        d="M55 18 C 65 16, 73 22, 71 30 C 69 37, 60 39, 53 35 C 47 31, 48 21, 55 18 Z"
        className={ACCENT}
      />
      {/* SMALLER globe — bias left, ~60% of canvas, wobbly bezier */}
      <path
        d="M14 28 C 14 19, 21 12, 30 12 C 39 12, 46 19, 46 28 C 46 37, 39 44, 30 44 C 21 44, 14 37, 14 28 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* equator — hairline */}
      <path d="M14 28 C 24 27.5, 36 28.5, 46 28" className={INK} strokeWidth={1} fill="none" />
      {/* meridian — hairline */}
      <path d="M30 12 C 24 19, 24 37, 30 44" className={INK} strokeWidth={1} fill="none" />
      {/* CONFIDENT scan sweep across globe — primary */}
      <path
        d="M16 38 C 22 32, 30 28, 38 22 C 42 19, 46 17, 49 16"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* scan tip dot */}
      <circle cx="49" cy="16" r="1.4" className={INK_FILL} />
    </svg>
  );
}

/* 16. GeneratingLineIcon — document outline + warm-accent sparkle escaping */
export function GeneratingLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent organic sparkle */}
      <path
        d="M59 14 L 61 20 L 67 22 L 63 24 L 65 30 L 60 27 L 56 32 L 56 25 L 50 24 L 56 21 Z"
        className={ACCENT}
      />
      {/* document — primary */}
      <path
        d="M14 13 C 14 11.8, 14.8 11, 16 11 L 44 11.2 C 45.2 11.3, 46 12.2, 45.9 13.4 L 45.5 43.5 C 45.4 44.7, 44.5 45.5, 43.3 45.4 L 15 45.1 C 13.8 45, 13 44.1, 13.1 42.9 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* text lines — varying weight */}
      <path d="M19 22 C 25 21.7, 31 22, 37 21.8" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M19 28 C 24 27.7, 29 28, 34 27.8" className={INK} strokeWidth={1.2} fill="none" />
      <path d="M19 34 C 23 33.8, 27 34, 30 33.9" className={INK} strokeWidth={1.2} fill="none" />
      {/* trailing sparkle dot */}
      <circle cx="49" cy="33" r="1.2" className={INK_FILL} />
    </svg>
  );
}

/* 17. ScheduleLineIcon — clock face with warm-accent hour blob */
export function ScheduleLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent hour-marker blob */}
      <path
        d="M52 16 C 56 15, 59 17, 58.5 20 C 58 23, 53 24, 50 22 C 47 20, 48 16.5, 52 16 Z"
        className={ACCENT}
      />
      {/* clock face — primary, wobbly bezier circle */}
      <path
        d="M16 28 C 16 16, 26 6, 38 6 C 50 6, 60 16, 60 28 C 60 40, 50 50, 38 50 C 26 50, 16 40, 16 28 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* clock hands — primary, slight wobble */}
      <path d="M38 14 C 38 19, 38 24, 38 28" className={INK} strokeWidth={2} fill="none" />
      <path d="M38 28 C 42 29.5, 46 31, 49 32" className={INK} strokeWidth={1.5} fill="none" />
      {/* center dot */}
      <circle cx="38" cy="28" r="1.6" className={INK_FILL} />
      {/* tick marks — hairline */}
      <path d="M38 8 L 38 11" className={INK} strokeWidth={1} fill="none" />
      <path d="M38 45 L 38 48" className={INK} strokeWidth={1} fill="none" />
      <path d="M18 28 L 21 28" className={INK} strokeWidth={1} fill="none" />
    </svg>
  );
}

/* 18. HelpDocsLineIcon — book with warm-accent "?" loop */
export function HelpDocsLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent question-mark organic blob — single shape, hook + tail */}
      <path
        d="M52 11 C 60 10, 66 14, 64 20 C 62 24, 58 25, 57 28 C 56.6 30, 56 32, 56 34 C 56.5 36, 53 37, 52 35 C 51.4 32, 52 29, 54 27 C 56 25, 60 23, 60 19 C 60 15, 55 13, 52 16 C 51 14, 51 12, 52 11 Z"
        className={ACCENT}
      />
      {/* book — primary */}
      <path
        d="M14 13 C 14 11.8, 14.8 11.1, 16 11.2 L 38 11.4 C 39.2 11.5, 40 12.4, 39.9 13.6 L 39.5 43.5 C 39.4 44.7, 38.5 45.5, 37.3 45.4 L 15 45.1 C 13.8 45, 13 44.1, 13.1 42.9 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* text lines — varying */}
      <path d="M19 22 C 25 21.7, 31 22, 35 21.8" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M19 28 C 24 27.7, 29 28, 33 27.8" className={INK} strokeWidth={1.2} fill="none" />
      <path d="M19 34 C 23 33.8, 27 34, 30 33.9" className={INK} strokeWidth={1.2} fill="none" />
    </svg>
  );
}

/* 19. CampaignSuccessLineIcon (120×80) — rising bars + warm-accent ribbon medal */
export function CampaignSuccessLineIcon(props: IconProps) {
  return (
    <svg {...HERO} {...props}>
      {/* warm-accent medal blob — organic */}
      <path
        d="M86 14 C 94 13, 100 18, 100 25 C 99.6 31, 92 34, 84 32 C 78 30, 76 22, 80 18 C 82 16, 84 14.5, 86 14 Z"
        className={ACCENT}
      />
      {/* bars — primary */}
      <path d="M22 64 C 22 58, 22 52, 22 50" className={INK} strokeWidth={3} fill="none" />
      <path d="M40 64 C 40 56, 40 46, 40 42" className={INK} strokeWidth={3} fill="none" />
      <path d="M58 64 C 58 50, 58 38, 58 34" className={INK} strokeWidth={3} fill="none" />
      <path d="M76 64 C 76 46, 76 32, 76 26" className={INK} strokeWidth={3} fill="none" />
      {/* baseline */}
      <path d="M14 68 C 36 67.6, 90 68.2, 106 67.7" className={INK} strokeWidth={1.5} fill="none" />
      {/* ribbon stars — hairline within medal */}
      <path
        d="M82 32 L 78 42 L 86 38 L 92 46 L 90 34"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* punctuation */}
      <circle cx="78" cy="42" r="1.2" className={INK_FILL} />
    </svg>
  );
}

/* 20. WelcomeLineIcon (120×80) — coffee mug with warm-accent organic coffee silhouette + ink steam wisp */
export function WelcomeLineIcon(props: IconProps) {
  return (
    <svg {...HERO} {...props}>
      {/* warm-accent coffee surface — organic blob inside the mug */}
      <path
        d="M48 36 C 56 34, 68 34, 76 35 C 78 36, 78 39, 76 40 C 68 41, 56 41, 48 40 C 46 39, 46 37, 48 36 Z"
        className={ACCENT}
      />
      {/* steam wisp — confident single ink curve, wobbly */}
      <path
        d="M58 8 C 60 14, 56 18, 59 24 C 62 30, 56 32, 58 38"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* second steam wisp — hairline, opposite curl */}
      <path
        d="M68 12 C 66 17, 70 20, 67 26 C 64 30, 68 32, 66 36"
        className={INK}
        strokeWidth={1}
        fill="none"
      />
      {/* mug — primary */}
      <path
        d="M44 32 C 44 30.8, 44.7 30.2, 45.7 30.3 L 78.3 30.5 C 79.3 30.6, 80 31.5, 79.9 32.5 L 79.4 58 C 79.3 64, 73 70, 67 70 L 57 70 C 51 70, 44.4 64, 44.5 58 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* handle — secondary */}
      <path
        d="M80 38 C 86 38, 90 41, 90 46 C 90 51, 86 54, 80 54"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* saucer — hairline */}
      <path d="M40 75 C 56 74.4, 70 75.2, 84 74.6" className={INK} strokeWidth={1.5} fill="none" />
    </svg>
  );
}

/* 21. IntegrationsLineIcon — ONE sweeping line through three wobbly tiles, middle filled */
export function IntegrationsLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent middle tile */}
      <path
        d="M34 26 C 34 25, 34.7 24.4, 35.7 24.5 L 47 24.6 C 48 24.7, 48.7 25.5, 48.6 26.5 L 48.3 38.5 C 48.2 39.6, 47.4 40.2, 46.4 40.1 L 35 39.9 C 34 39.8, 33.4 39, 33.5 38 Z"
        className={ACCENT}
      />
      {/* left tile — secondary */}
      <path
        d="M14 16 C 14 15, 14.7 14.4, 15.7 14.5 L 27 14.6 C 28 14.7, 28.7 15.5, 28.6 16.5 L 28.3 28.5 C 28.2 29.6, 27.4 30.2, 26.4 30.1 L 15 29.9 C 14 29.8, 13.4 29, 13.5 28 Z"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* right tile — secondary */}
      <path
        d="M52 36 C 52 35, 52.7 34.4, 53.7 34.5 L 65 34.6 C 66 34.7, 66.7 35.5, 66.6 36.5 L 66.3 48.5 C 66.2 49.6, 65.4 50.2, 64.4 50.1 L 53 49.9 C 52 49.8, 51.4 49, 51.5 48 Z"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* ONE continuous sweeping ink line through all three — primary */}
      <path
        d="M21 30 C 25 32, 30 28, 34 28 C 38 28, 42 33, 46 36 C 50 39, 54 38, 58 38"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* connection dots */}
      <circle cx="21" cy="30" r="1.4" className={INK_FILL} />
      <circle cx="58" cy="38" r="1.4" className={INK_FILL} />
    </svg>
  );
}

/* 22. DocumentsLineIcon — stack of two wobbly docs + warm-accent paper-clip blob */
export function DocumentsLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent paper-clip organic blob */}
      <path
        d="M51 11 C 56 10, 60 12, 59.5 16 C 59 19, 54 20, 51 18 C 48 16, 48 12, 51 11 Z"
        className={ACCENT}
      />
      {/* front doc — primary */}
      <path
        d="M22 18 C 22 16.8, 22.8 16, 24 16 L 52 16.2 C 53.2 16.3, 54 17.2, 53.9 18.4 L 53.5 47.5 C 53.4 48.7, 52.5 49.5, 51.3 49.4 L 23 49.1 C 21.8 49, 21 48.1, 21.1 46.9 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* back doc — secondary */}
      <path
        d="M14 12 C 14 11, 14.7 10.4, 15.6 10.5 L 44 10.7 C 45 10.8, 45.7 11.6, 45.6 12.5 L 45.2 41.5"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* text lines — varying */}
      <path d="M28 26 C 34 25.7, 40 26, 47 25.8" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M28 32 C 34 31.7, 40 32, 47 31.8" className={INK} strokeWidth={1.2} fill="none" />
      <path d="M28 38 C 33 37.8, 38 38, 42 37.9" className={INK} strokeWidth={1.2} fill="none" />
    </svg>
  );
}

/* 23. DiscoverLineIcon — newspaper + warm-accent magnifying-glass blob lens */
export function DiscoverLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent lens blob */}
      <path
        d="M50 11 C 58 10, 64 14, 63 20 C 62 26, 55 27, 49 24 C 44 21, 44 13, 50 11 Z"
        className={ACCENT}
      />
      {/* newspaper — primary */}
      <path
        d="M14 18 C 14 16.8, 14.8 16, 16 16 L 46 16.2 C 47.2 16.3, 48 17.2, 47.9 18.4 L 47.5 43.5 C 47.4 44.7, 46.5 45.5, 45.3 45.4 L 15 45.1 C 13.8 45, 13 44.1, 13.1 42.9 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* headline + body lines — varying */}
      <path d="M19 24 C 25 23.7, 31 24, 37 23.8" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M19 30 C 25 29.7, 31 30, 41 29.8" className={INK} strokeWidth={1.2} fill="none" />
      <path d="M19 36 C 23 35.8, 27 36, 30 35.9" className={INK} strokeWidth={1.2} fill="none" />
      {/* magnifier handle — primary */}
      <path d="M58 24 C 62 28, 64 30, 67 33" className={INK} strokeWidth={2} fill="none" />
    </svg>
  );
}

/* 24. RepurposeLineIcon — source rect filled + three wobbly arrows fanning to smaller rects */
export function RepurposeLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent source rect — organic */}
      <path
        d="M14 22 C 14 20.8, 14.8 20.1, 16 20.2 L 30 20.4 C 31.2 20.5, 32 21.4, 31.9 22.6 L 31.6 35.5 C 31.5 36.7, 30.6 37.4, 29.4 37.3 L 15 37 C 13.8 36.9, 13 36, 13.1 34.8 Z"
        className={ACCENT}
      />
      {/* target rects — secondary, wobbled corners + bowed edges */}
      <path
        d="M50 12 C 50 11.2, 50.6 10.8, 51.4 10.9 L 63.8 11 C 64.7 11.1, 65.1 11.7, 65 12.5 L 64.8 20.4 C 64.7 21.2, 64 21.6, 63.2 21.5 L 50.8 21.3 C 50 21.2, 49.6 20.6, 49.7 19.8 Z"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      <path
        d="M50 25 C 50 24.2, 50.6 23.8, 51.4 23.9 L 63.8 24 C 64.7 24.1, 65.1 24.7, 65 25.5 L 64.8 33.4 C 64.7 34.2, 64 34.6, 63.2 34.5 L 50.8 34.3 C 50 34.2, 49.6 33.6, 49.7 32.8 Z"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      <path
        d="M50 38 C 50 37.2, 50.6 36.8, 51.4 36.9 L 63.8 37 C 64.7 37.1, 65.1 37.7, 65 38.5 L 64.8 46.4 C 64.7 47.2, 64 47.6, 63.2 47.5 L 50.8 47.3 C 50 47.2, 49.6 46.6, 49.7 45.8 Z"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* arrows — primary */}
      <path d="M33 25 C 39 19, 44 16, 48 15" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M45 13 L 48 15 L 46 18" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M33 29 C 40 29, 44 29, 48 29" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M45 27 L 48 29 L 46 32" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M33 33 C 40 39, 44 41, 48 42" className={INK} strokeWidth={1.5} fill="none" />
      <path d="M45 40 L 48 42 L 46 45" className={INK} strokeWidth={1.5} fill="none" />
    </svg>
  );
}

/* 25. VoiceLineIcon — microphone with warm-accent capsule + radiating soundwaves */
export function VoiceLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent mic capsule — organic */}
      <path
        d="M30 12 C 36 11, 42 13, 42 18 L 41.6 28 C 41.5 32, 37 34, 32 33 C 28 32, 26 28, 26.5 24 L 27 16 C 27 14, 28 12.5, 30 12 Z"
        className={ACCENT}
      />
      {/* mic stand cradle — primary */}
      <path
        d="M22 24 C 22 30, 26 36, 33 37 C 40 38, 46 34, 47 28"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* mic stem — primary */}
      <path d="M34 38 C 34 41, 34 44, 34 46" className={INK} strokeWidth={2} fill="none" />
      {/* base */}
      <path d="M28 46 C 32 45.5, 36 45.5, 40 46" className={INK} strokeWidth={1.5} fill="none" />
      {/* sound waves — varying weight */}
      <path d="M52 18 C 55 21, 56 26, 55 30" className={INK} strokeWidth={1.2} fill="none" />
      <path d="M58 14 C 62 19, 63 27, 60 33" className={INK} strokeWidth={1} fill="none" />
    </svg>
  );
}

/* 26. FilesLineIcon — manila folder with one document peeking out the top,
 *     warm-accent label sticker on the folder. Distinct from `DocumentsLineIcon`
 *     (stack of authored docs) — Files represents a project's reference bucket:
 *     uploads, generated assets, links — one folder, one paper sticking out.
 *
 *     Composition matches the canonical line-illustration language:
 *       1. Document inside (drawn first, primary stroke, hairline body lines)
 *       2. Folder front body with tab on top-left edge (heavier primary stroke,
 *          covers bottom half of document — gives depth without fill)
 *       3. Warm-accent rounded label sticker (front of folder, lower-right)
 */
export function FilesLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* document inside — drawn first, sticks out above the folder front lip */}
      <path
        d="M22 13 C 22 11.8, 22.8 11, 24 11 L 50 11.2 C 51.2 11.3, 52 12.2, 51.9 13.4 L 51.6 32 L 22.4 32 Z"
        className={INK}
        strokeWidth={1.5}
        fill="none"
      />
      {/* document body lines — varying widths, slight droop */}
      <path d="M27 18 C 32 17.7, 38 18, 45 17.8" className={INK} strokeWidth={1.2} fill="none" />
      <path d="M27 23 C 31 22.8, 36 23, 42 22.9" className={INK} strokeWidth={1.1} fill="none" />
      <path d="M27 28 C 30 27.8, 33 28, 36 27.9" className={INK} strokeWidth={1.1} fill="none" />

      {/* folder front body with tab on top-left edge — heaviest stroke, covers
          bottom of document so doc reads as "inside" */}
      <path
        d="M13 26 C 13 24.8, 13.8 24, 15 24 L 25 24.1 C 26 24.1, 26.6 24.5, 27 25.3 L 27.6 26.5 C 28 27.3, 28.6 27.7, 29.6 27.7 L 63 27.9 C 64.2 28, 65 28.9, 64.9 30.1 L 64.5 47 C 64.4 48.3, 63.4 49.1, 62.1 49 L 14 48.7 C 12.7 48.6, 12 47.7, 12.1 46.4 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />

      {/* warm-accent label sticker — small rounded blob on folder front,
          lower-right, signals "labeled / categorized" */}
      <path
        d="M48 38 C 56 37.4, 60 39, 60 42 C 60 45, 55 45.6, 49 45 C 44 44.4, 43 38.6, 48 38 Z"
        className={ACCENT}
      />
    </svg>
  );
}

/* 27. ConversationsLineIcon — two overlapping speech bubbles (a thread), the
 *     warm-accent one is the reply. Distinct from `EngagementLineIcon` (one
 *     bubble + heart = social replies) — Conversations is the project's chat
 *     history: an outlined "you" bubble with dots + a filled warm "reply". */
export function ConversationsLineIcon(props: IconProps) {
  return (
    <svg {...SMALL} {...props}>
      {/* warm-accent reply bubble — filled organic blob, lower-right, tail down */}
      <path
        d="M43 26 C 43 24.3, 44.3 23.2, 46 23.3 L 63 23.6 C 64.7 23.7, 65.8 25, 65.7 26.7 L 65.4 37.4 C 65.3 39.1, 64 40.2, 62.3 40.1 L 57 40 L 60 47 L 50 39.9 L 46 39.8 C 44.3 39.8, 43 38.5, 43 36.8 Z"
        className={ACCENT}
      />
      {/* back conversation bubble — ink outline, upper-left, tail down-left */}
      <path
        d="M12 16 C 12 14.2, 13.2 13, 15 13.1 L 46 13.4 C 47.8 13.5, 48.9 14.8, 48.8 16.6 L 48.5 28.4 C 48.4 30.2, 47.1 31.3, 45.3 31.2 L 25 30.9 L 17 38 L 18.3 30.8 L 15 30.7 C 13.2 30.6, 12 29.4, 12 27.6 Z"
        className={INK}
        strokeWidth={2}
        fill="none"
      />
      {/* conversation dots inside the back bubble — slight size variance */}
      <circle cx="20" cy="22" r="1.5" className={INK_FILL} />
      <circle cx="27" cy="22" r="1.4" className={INK_FILL} />
      <circle cx="34" cy="22" r="1.5" className={INK_FILL} />
    </svg>
  );
}

/* ───────────────────────────────────────────────────────────
 * Backward-compat aliases — preserve the 21 legacy exports so
 * existing import sites continue to compile.
 * ─────────────────────────────────────────────────────────── */
export const ConversationsIllustration = ConversationsLineIcon;
export const CreatePostIllustration = CreatePostLineIcon;
export const AIGenerateIllustration = AIGenerateLineIcon;
export const TemplateIllustration = TemplateLineIcon;
export const ProductIllustration = ProductLineIcon;
export const FillCalendarIllustration = FillCalendarLineIcon;
export const CampaignIllustration = CampaignLineIcon;
export const MediaLibraryIllustration = MediaLibraryLineIcon;
export const AnalyticsIllustration = AnalyticsLineIcon;
export const EngagementIllustration = EngagementLineIcon;
export const NotFoundIllustration = NotFoundLineIcon;
export const EmptyChartIllustration = EmptyChartLineIcon;
export const BrandKitIllustration = BrandKitLineIcon;
export const CalendarEmptyIllustration = CalendarEmptyLineIcon;
export const PlanHeroIllustration = PlanHeroLineIcon;
export const WebsiteScanIllustration = WebsiteScanLineIcon;
export const GeneratingIllustration = GeneratingLineIcon;
export const ScheduleIllustration = ScheduleLineIcon;
export const HelpDocsIllustration = HelpDocsLineIcon;
export const CampaignSuccessIllustration = CampaignSuccessLineIcon;
export const WelcomeIllustration = WelcomeLineIcon;
export const IntegrationsIllustration = IntegrationsLineIcon;
